import { VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

class VariantPicker extends HTMLElement {
  #abortController;
  #boundOnVariantChanged;
  #rechargeWidgetObserver = null;

  connectedCallback() {
    this.#boundOnVariantChanged = this.#onVariantChanged.bind(this);
    this.addEventListener('change', this.#boundOnVariantChanged);

    this.#bindRechargeControls();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#boundOnVariantChanged);

    this.#rechargeWidgetObserver?.disconnect();
    this.#rechargeWidgetObserver = null;
  }

  get #enableHistoryState() {
    return this.getAttribute('enable-history-state') === 'true';
  }

  get #productId() {
    return this.getAttribute('data-product-id');
  }

  get #productUrl() {
    return this.getAttribute('data-product-url');
  }

  get #sectionId() {
    return this.getAttribute('data-section-id');
  }

  get #selectedOptionValues() {
    const inputElements = Array.from(this.querySelectorAll('fieldset input:checked'));
    const selectElements = Array.from(this.querySelectorAll('select'));

    return [
      ...selectElements.map((select) => select.options[select.selectedIndex]),
      ...inputElements,
    ].map((option) => option.getAttribute('data-option-value-id')).filter(Boolean);
  }

  // Recharge-powered
  get #sellingPlanInput() {
    return this.closest('form')?.querySelector('[name="selling_plan"][type="hidden"]');
  }

  get #selectedSellingPlanId() {
    const val = this.#sellingPlanInput?.value;
    return val || null;
  }

  #onVariantChanged(event) {
    const target = event.target;
    const input = target instanceof HTMLSelectElement ? target.options[target.selectedIndex] : target;

    if (!(input instanceof HTMLElement)) return;

    const variantId = input.getAttribute('data-variant-id');

    this.#fetchUpdatedSection();
    this.#updateHistoryState(variantId);
  }

  /**
   * Builds the Section Rendering API request URL.
   * @returns {string}
   */
  #buildRequestUrl() {
    const params = new URLSearchParams();

    if (this.#sectionId) {
      params.set('section_id', this.#sectionId);
    }

    const optionValues = this.#selectedOptionValues;
    if (optionValues.length) {
      params.set('option_values', optionValues.join(','));
    }

    const sellingPlanId = this.#selectedSellingPlanId;
    if (sellingPlanId) {
      params.set('selling_plan', sellingPlanId);
    }

    return `${this.#productUrl}?${params}`;
  }

  /**
   * Fetches the updated section, optionally morphs the variant picker, and dispatches a variant update event.
   * @param {Object} options
   * @param {boolean} [options.morph=true] - Whether to morph the variant picker with the new HTML.
   */
  #fetchUpdatedSection({ morph = true } = {}) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(this.#buildRequestUrl(), { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
        const variantData = JSON.parse(
          parsedHtml.querySelector('variant-picker script[type="application/json"]')?.textContent ?? 'null'
        );

        if (morph) {
          this.#morphVariantPicker(parsedHtml);
        }

        this.dispatchEvent(
          new VariantUpdateEvent(variantData, this.#sectionId, {
            html: parsedHtml,
            productId: this.#productId,
            source: morph ? 'variant' : 'selling-plan',
          })
        );
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * Morphs the variant picker with the new HTML from the Section Rendering API.
   * @param {Document} parsedHtml
   */
  #morphVariantPicker(parsedHtml) {
    const sourceElement = parsedHtml.querySelector(this.tagName.toLowerCase());
    if (!sourceElement) return;

    morph(this, sourceElement);
  }

  /**
   * Updates the browser URL with the selected variant.
   * @param {string} variantId
   */
  #updateHistoryState(variantId) {
    if (!this.#enableHistoryState) return;

    const url = new URL(window.location.href);

    variantId
      ? url.searchParams.set('variant', variantId)
      : url.searchParams.delete('variant');

    history.replaceState({}, '', url.toString());
  }

  /**
   * Binds the Recharge Legacy Widget by intercepting the hidden selling plan input setter.
   */
  #bindRechargeControls() {
    const input = this.#sellingPlanInput;

    if (input) {
      this.#interceptSellingPlanSetter(input);
      return;
    }

    this.#rechargeWidgetObserver = new MutationObserver(() => {
      const input = this.#sellingPlanInput;
      if (!input) return;

      this.#interceptSellingPlanSetter(input);
      this.#rechargeWidgetObserver?.disconnect();
      this.#rechargeWidgetObserver = null;
    });

    this.#rechargeWidgetObserver.observe(
      this.closest('.shopify-section') ?? document.body,
      { childList: true, subtree: true }
    );
  }

  /**
   * Triggers a re-fetch when Recharge Legacy Widget changes the hidden selling plan input.
   */
  #interceptSellingPlanSetter(input) {
    const descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

    Object.defineProperty(input, 'value', {
      get: descriptor.get,
      set: (val) => {
        descriptor.set.call(input, val);
        this.#fetchUpdatedSection({ morph: false });
      },
      configurable: true,
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
